// Librerias
import { keyBy } from 'lodash'

// Stores
import { useUsersStore, useSprintsStore, useReportsStore } from '@/stores/index.js'

const getLocationFranchiseIds = (location) => {
  if (Array.isArray(location.franchise_ids)) {
    return location.franchise_ids.map(Number).filter(Boolean)
  }

  if (Array.isArray(location.franchises) && location.franchises.length > 0) {
    return location.franchises.map(f => Number(f.id)).filter(Boolean)
  }

  return []
}

const verifyLocation = (location) => {
  const usersStore = useUsersStore()
  if (!usersStore.current) return false

  if (usersStore.current.is_organization_admin) return true

  const franchiseIds = getLocationFranchiseIds(location)

  if (
    usersStore.current.is_franchise_admin ||
    usersStore.current.is_franchise_supervisor ||
    usersStore.current.is_users_admin
  ) {
    return (
      franchiseIds.some(id => usersStore.userFranchisesAdmin.includes(id)) ||
      franchiseIds.some(id => usersStore.userFranchisesSupervisor.includes(id)) ||
      franchiseIds.some(id => usersStore.userUsersAdmin.includes(id)) ||
      usersStore.userLocations.includes(location.id)
    )
  }

  return usersStore.userLocations.includes(location.id)
}


const sprintLocation = (location) => {
  const sprintsStore = useSprintsStore()
  const reportsStore = useReportsStore()

  const sprintId = reportsStore.reportQuery.sprint.id
  const sprint = sprintsStore.missionSprints.find(sprint => sprint.id === sprintId)
  if (sprint) {
    const locations = JSON.parse(sprint.locations_id)
    return locations ? locations.includes(location.id) : false
  }
  return false
}

const normalizeFranchiseFilter = (filter) => {
  if (filter === null || filter === undefined || filter === 0 || filter === '0') return []
  if (Array.isArray(filter)) return filter.map(Number).filter(Boolean)
  return [Number(filter)].filter(Boolean)
}

const matchFranchise = (location, filter) => {
  const filters = normalizeFranchiseFilter(filter)
  if (filters.length === 0) return true

  const locationFranchiseIds = getLocationFranchiseIds(location)
  return locationFranchiseIds.some(id => filters.includes(id))
}

// Recorre los grupos crudos del store a cualquier profundidad. `visit` recibe
// el grupo y su cadena de ancestros root-first, con el propio grupo al final.
const walkGroups = (groups, visit, chain = []) => {
  groups.forEach(group => {
    const groupChain = [...chain, group]
    visit(group, groupChain)
    walkGroups(group.subgroups || [], visit, groupChain)
  })
}

// Recorre nodos de un arbol ya construido, siguiendo `children`.
const walkTreeNodes = (nodes, visit, chain = []) => {
  nodes.forEach(node => {
    const nodeChain = [...chain, node]
    visit(node, nodeChain)
    walkTreeNodes(node.children || [], visit, nodeChain)
  })
}

// `top`/`middle`/`bottom` son alias de la cadena para los consumidores que aun
// los leen, con la misma convencion que el backend: top = raiz, middle = padre
// inmediato del grupo propio, bottom = grupo propio de la ubicacion.
const withGroupChain = (location, chain) => {
  const located = {
    ...location,
    group_chain: chain.map(group => ({ id: group.id, name: group.name }))
  }

  const top = chain[0] || null
  const middle = chain.length >= 2 ? chain[chain.length - 2] : null
  const bottom = chain[chain.length - 1] || null

  if (top) {
    located.top = top.id
    located.top_name = top.name
  }
  if (middle) {
    located.middle = middle.id
    located.middle_name = middle.name
  }
  if (bottom) {
    located.bottom = bottom.id
    located.bottom_name = bottom.name
  }

  return located
}

const buildGroupsAndLocationsTree = (groups, { filter, locationName, noTick }) => {
  const matchesName = (location) => {
    const term = String(locationName ?? '').toLowerCase()
    return location.name.toLowerCase().includes(term) || String(location.full_name).toLowerCase().includes(term)
  }

  const passesLocation = (location) => {
    if (!verifyLocation(location) || !matchesName(location)) return false
    return matchFranchise(location, filter)
  }

  const buildNode = (group, level) => {
    const childNodes = (group.subgroups || [])
      .map(subgroup => buildNode(subgroup, level + 1))
      .filter(Boolean)

    const childLocations = (group.locations || [])
      .filter(location => passesLocation(location))
      .map(location => ({ ...location, name: location.full_name }))

    const node = {
      ...group,
      id: 'group' + group.id,
      level,
      children: [...childNodes, ...childLocations]
    }

    if (noTick) node.noTick = true

    if (node.children.length > 0) return node
  }

  return groups.map(group => buildNode(group, 1)).filter(Boolean)
}

export default {
  getLocations: (state) => state.locations,
  locationsById: (state) => keyBy(state.locations, 'id'),
  allLocations (state) {
    const locations = []

    walkTreeNodes(state.groupsAsTree(), node => {
      (node.locations || []).forEach(location => locations.push(location))
    })

    return locations
  },
  keyedById: state => keyBy(state.allLocations, 'id'),
  allLocationsWithParents (state) {
    const locations = []

    walkTreeNodes(state.groupsAsTree(), (node, chain) => {
      (node.locations || []).forEach(location => locations.push(withGroupChain(location, chain)))
    })

    return locations
  },
  // Cadena de grupos root-first por id de grupo, incluyendo el propio grupo.
  groupChainById (state) {
    const chains = {}

    walkGroups(state.groups, (group, chain) => {
      chains[group.id] = chain.map((ancestor, index) => ({
        id: ancestor.id,
        name: ancestor.name,
        level: index + 1
      }))
    })

    return chains
  },
  // Rango de cada grupo en el orden real del admin (alfabetico o custom por
  // nivel, ya resuelto por el backend en el orden de state.groups/subgroups).
  // Sirve para reordenar arboles armados desde una lista plana externa
  // (buildChainTree) que no trae ese orden por si misma.
  groupOrderIndex (state) {
    const index = {}
    let rank = 0

    walkGroups(state.groups, (group) => {
      index[group.id] = rank++
    })

    return index
  },
  // Profundidad real del arbol de grupos de la organizacion.
  maxGroupDepth (state) {
    let depth = 0

    walkGroups(state.groups, (group, chain) => {
      depth = Math.max(depth, chain.length)
    })

    return depth
  },
  groupsKeyedById (state) {
    const groups = []

    walkGroups(state.groups, group => groups.push(group))

    return keyBy(groups, 'id')
  },
  availableLocations (state) {
    const locations = []

    walkGroups(state.groups, group => {
      (group.locations || [])
        .filter(location => verifyLocation(location))
        .forEach(location => locations.push(location.id))
    })

    return locations
  },
  createTreeNode: (state) => (group, level) => {
    const node = {
      ...group,
      id: `group${group.id}`,
      level,
      children: []
    }

    if (group.locations.length > 0) {
      const filteredLocations = group.locations.filter(location => {
        return sprintLocation(location)
      })
      node.children.push(...filteredLocations)
    }

    if (group.subgroups.length > 0) {
      const childNodes = group.subgroups.map(subgroup => {
        return state.createTreeNode(subgroup, level + 1)
      }).filter(childNode => childNode.children.length > 0)

      node.children.push(...childNodes)
    }

    return node
  },
  groupsAndLocationsAsTreeSprints (state) {
    return () => {
      const topLevelNodes = state.groups.map(group => {
        return state.createTreeNode(group, 1)
      }).filter(node => node.children.length > 0)

      return topLevelNodes
    }
  },
  groupsAndLocationsAsTree (state) {
    return (filter, locationName) => {
      return buildGroupsAndLocationsTree(state.groups, { filter, locationName, noTick: false })
    }
  },
  groupsAndLocationsAsTreeSinlge (state) {
    return (filter, locationName) => {
      return buildGroupsAndLocationsTree(state.groups, { filter, locationName, noTick: true })
    }
  },
  /**
   * @param {boolean} empty | si true, retorna grupos incluso sin ubicaciones que pasen el filtro.
   * @param {number|number[]|null} filter | IDs de franquicia a filtrar; null/0/[] = sin filtro.
   */
  groupsAsTree (state) {
    return (empty = false, filter = null) => {
      const passesFilter = (location) => verifyLocation(location) && matchFranchise(location, filter)

      const buildNode = (group, level) => {
        const subgroups = group.subgroups || []
        const locations = group.locations || []

        const children = subgroups
          .map(subgroup => buildNode(subgroup, level + 1))
          .filter(Boolean)

        const node = {
          ...group,
          id: group.id,
          level,
          children
        }

        // Un grupo raiz sin subgrupos no expone noTick.
        if (level > 1 || subgroups.length > 0) {
          node.noTick = children.some(child => child.noTick) ||
            locations.some(location => !verifyLocation(location) || !matchFranchise(location, filter))
        }

        if (empty || locations.some(location => passesFilter(location)) || children.length > 0) {
          return node
        }
      }

      return state.groups.map(group => buildNode(group, 1)).filter(Boolean)
    }
  }
}
